import React from 'react';
import { Check } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

const HowTo = () => {
  const howToData = [
    {
      title: "How to Trademark a Name",
      description: "Trademarking your name is an essential step to protect your brand and ensure legal rights over its use in commerce. Here are some key steps to trademark a name:",
      steps: [
        { number: 1, title: "Conduct a Trademark Search", description: "Make sure the name is not already in use by another business or trademarked." },
        { number: 2, title: "Choose the Right Trademark Class", description: "Identify the appropriate trademark class that matches your product or service." },
        { number: 3, title: "Prepare Your Application", description: "Complete the application form, ensuring all information is accurate and clear." },
        { number: 4, title: "Submit Your Application", description: "File your trademark application with the relevant intellectual property office, such as the USPTO." },
        { number: 5, title: "Wait for Approval", description: "After submission, the trademark office will examine your application, and if approved, your name will be officially trademarked." }
      ]
    },
    {
      title: "How to Trademark a Logo",
      image: "https://images.unsplash.com/photo-1758519288480-1489c17b1519",
      description: "Trademarking a logo helps protect your brand's unique design, ensuring that no one else can use a similar logo in commerce. By registering your logo, you secure exclusive rights to it, making it easier to prevent misuse or confusion in the market.",
      additionalText: "The process involves submitting a high-quality image of the logo and describing how it's used, ultimately granting legal protection to your brand's visual identity."
    },
    {
      title: "How to Trademark a Slogan",
      image: "https://images.unsplash.com/photo-1758599543129-5269a8f29e68",
      description: "Trademarking a slogan protects your brand's unique phrase, giving you exclusive rights to use it in connection with your products or services. A trademarked slogan helps distinguish your brand from competitors and prevents others from using a similar phrase.",
      additionalText: "To trademark a slogan, it must be distinctive and used in commerce, ensuring your brand's message remains protected and recognizable in the marketplace."
    }
  ];

  const renderHowToCard = (item, index) => (
    <div key={index} className="bg-gradient-to-br from-emerald-50 to-blue-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 h-full">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">{item.title}</h3>
      
      {item.image && (
        <div className="mb-6">
          <img
            src={item.image}
            alt={`${item.title} process`}
            className="w-full h-48 object-cover rounded-xl mb-6"
          />
        </div>
      )}
      
      <p className="text-gray-700 mb-6 leading-relaxed">{item.description}</p>
      
      {item.steps ? (
        <ul className="space-y-4">
          {item.steps.map((step, stepIndex) => (
            <li key={stepIndex} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold mt-0.5">
                {step.number}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{step.title}</p>
                <p className="text-base text-gray-600">{step.description}</p>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-gray-700 leading-relaxed">{item.additionalText}</p>
      )}
    </div>
  );

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4 px-4 py-2 bg-emerald-100 text-emerald-800 rounded-full text-xl font-semibold">
            Trademark Guide
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Complete Guide to Trademark Protection
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Whether you're protecting a business name, distinctive logo, or memorable slogan, understanding the trademark process is essential for safeguarding your brand identity. Our comprehensive guide walks you through each step to ensure your intellectual property receives the legal protection it deserves.
          </p>
        </div>

        {/* Desktop Grid - Hidden on mobile/tablet */}
        <div className="hidden lg:grid grid-cols-3 gap-12 max-w-7xl mx-auto">
          {howToData.map((item, index) => renderHowToCard(item, index))}
        </div>

        {/* Mobile/Tablet Slider - Hidden on desktop */}
        <div className="lg:hidden">
          <Swiper
            modules={[Pagination, Autoplay]}
            spaceBetween={20}
            pagination={{
              clickable: true,
              bulletClass: 'swiper-pagination-bullet !bg-emerald-600',
              bulletActiveClass: 'swiper-pagination-bullet-active'
            }}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            breakpoints={{
              320: {
                slidesPerView: 1,
                spaceBetween: 20
              },
              768: {
                slidesPerView: 1,
                spaceBetween: 20
              }
            }}
            className="howto-slider"
          >
            {howToData.map((item, index) => (
              <SwiperSlide key={index}>
                {renderHowToCard(item, index)}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default HowTo;